import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  Timeline,
  CTASection,
  Reveal,
} from './kit.jsx'

const ENTRIES = [
  {
    date: 'Jul 9, 2026',
    tag: 'New',
    title: 'Gmail outreach and the unified Inbox',
    body: 'Connect your own Gmail and run every sequence as yourself — sends, opens, and replies now flow into one place instead of scattering across tabs.',
    bullets: [
      'One-click Gmail connect with per-user OAuth — no shared blast domains',
      'Replies land in a unified Inbox and auto-log against the company timeline',
      'Sequences pause the moment a prospect replies, so no one gets double-messaged',
      'Sending stays under your own daily Gmail limits to protect deliverability',
    ],
  },
  {
    date: 'Jun 18, 2026',
    tag: 'New',
    title: 'Events, Activity, and Deal Flow',
    body: 'A people-level pipeline for the whole book of business. Every company moves from new to contacted to quoted to won, with the activity feed writing itself as reps work.',
    bullets: [
      'Drag companies across the Deal Flow board from New through Closed-Won',
      'Activity feed captures every email, reply, stage change, and note automatically',
      'Events roll up by rep and region so managers see momentum at a glance',
      'Won/lost outcomes feed straight into the Data views for reporting',
    ],
  },
  {
    date: 'May 27, 2026',
    tag: 'New',
    title: 'Data insights — from Overview to Market Map',
    body: 'Five analytics views that turn 10,942 leads and your outreach into decisions: Overview, Category Breakdown, Region Performance, Outreach Health, and the Market Map.',
    bullets: [
      'Overview surfaces pipeline value, reply rate, and deals won in one glance',
      'Region Performance ranks all 50 U.S. regions by coverage and conversion',
      'Category Breakdown splits each vertical out on every metric',
      'Market Map plots your territory so reps see where the density really is',
    ],
  },
  {
    date: 'Apr 30, 2026',
    tag: 'Improved',
    title: 'Dark and light themes, the in-app assistant, and notifications',
    body: 'The workspace now follows your system theme, ships a built-in assistant that answers from your live data, and keeps you posted when a prospect replies.',
    bullets: [
      'Full dark and light themes with a persistent per-user preference',
      'Ask the assistant "top regions this quarter" or "how is outreach doing" in plain English',
      'Real-time notifications for new replies, stage changes, and teammate assignments',
      'Answers pull from your own workspace — no exports, no stale snapshots',
    ],
  },
  {
    date: 'Mar 24, 2026',
    tag: 'New',
    title: 'Multi-step Sequence builder and onboarding wizard',
    body: 'Build Apollo-style cadences with intro emails, timed waits, follow-ups, and break-ups — then get productive in minutes with a guided setup.',
    bullets: [
      'Visual sequence builder with steps, waits, and branch conditions',
      'Reusable templates so a proven cadence is one click away for every rep',
      'Onboarding wizard connects Gmail, picks a region, and loads a first list',
      'Preview and test-send any step before a single prospect hears from you',
    ],
  },
  {
    date: 'Feb 20, 2026',
    tag: 'New',
    title: 'Team collaboration and invites',
    body: 'Bring the whole crew into one workspace. Invite reps, assign regions, and keep everyone working the same source of truth instead of trading spreadsheets.',
    bullets: [
      'Email invites with role-based access for owners, managers, and reps',
      'Assign regions and companies so territories never overlap',
      'Shared pipeline and activity, scoped by who owns what',
      'Seat management from a single team settings page',
    ],
  },
  {
    date: 'Jan 28, 2026',
    tag: 'New',
    title: 'Revailo Sales launches — 10,942 verified leads live via API',
    body: 'The first release. Every company, cleaned and organized by industry and region, served through a fast paginated API behind Cognito auth.',
    bullets: [
      '10,942 verified companies — 10,942 companies across the verticals you sell into',
      'Per-category tables and a paginated Lambda API load any region in under a second',
      'Sortable, searchable lead tables with contacts, service area, size, and volume',
      'Secure sign-in on Cognito from day one',
    ],
  },
]

export default function ChangelogPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="Changelog"
        description="What's new in Revailo Sales — Gmail outreach, Deal Flow, data insights, sequences, team collaboration, and the release of 10,942 verified B2B leads."
        path="/changelog"
        keywords="Revailo Sales changelog, product updates, release notes, sales CRM changelog"
      />

      <Section>
        <PageHero
          eyebrow="Changelog"
          title={<>Every shipment, in order.</>}
          lead="A running log of what we've built into Revailo Sales — outreach, pipeline, data views, and the 10,942-lead database underneath it all. Newest first."
          primary={{ label: 'Start free', to: '/signup' }}
          secondary={{ label: 'See features', to: '/features' }}
        />
      </Section>

      <Section sm>
        <Container>
          <Reveal>
            <Timeline items={ENTRIES} />
          </Reveal>
        </Container>
      </Section>

      <CTASection
        title="See what shipped, then put it to work."
        body="Every feature on this page is live in your workspace on day one. Start free and see every qualified company in your market today."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      />
    </MarketingShell>
  )
}
